import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';
import Footer from '../components/Footer';
import BrandMarquee from '../components/BrandMarquee';
import { optimizedCover, coverSrcSet } from '../utils/imageUtils';
import { useTheme } from '../contexts/ThemeContext';
import { cachedGet } from '../utils/apiCache';
import { bookPath } from '../utils/bookUrl';

const BACKEND_URL = API_BASE_URL;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { sliderConfig } = useTheme();
  const isRTL = i18n.language === 'ar';
  
  const [newReleases, setNewReleases] = useState([]);
  const [reprints, setReprints] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [sliderItems, setSliderItems] = useState([]);
  const [homepageSections, setHomepageSections] = useState([]);
  const [sectionBooks, setSectionBooks] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [siteSettings, setSiteSettings] = useState({
    site_name: 'İnsan Yayınları'
  });
  const [brands, setBrands] = useState([]);

  const [sectionsLoaded, setSectionsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => { if (!cancelled) setLoading(false); }, 5000);
    const loadData = async () => {
      try {
        await fetchSliders();
      } catch {}
      if (!cancelled) setLoading(false);
      fetchBooks().catch(() => {});
      fetchHomepageSections().then(() => {
        if (!cancelled) setSectionsLoaded(true);
      }).catch(() => {
        if (!cancelled) setSectionsLoaded(true);
      });
      fetchSiteSettings();
      try {
        const res = await cachedGet(`${BACKEND_URL}/api/brands`);
        if (!cancelled) {
          const data = res.data;
          const list = Array.isArray(data) ? data : data.brands || data.data || [];
          setBrands(list.filter(b => b.name));
        }
      } catch {}
    };
    loadData();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await cachedGet(`${BACKEND_URL}/api/settings/`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchSliders = async () => {
    try {
      const res = await cachedGet(`${BACKEND_URL}/api/sliders`);
      const activeSliders = (res.data || []).filter(s => s.is_active);
      setSliderItems(activeSliders);
    } catch (error) {
      console.error('Error fetching sliders:', error);
    }
  };

  const fetchHomepageSections = async () => {
    try {
      // Single request to get all sections with their books (fast, cached on server)
      const res = await axios.get(`${BACKEND_URL}/api/homepage-sections/with-books`, { timeout: 15000 });
      const sectionsWithBooks = res.data || [];
      setHomepageSections(sectionsWithBooks);
      const booksMap = {};
      for (const sec of sectionsWithBooks) {
        booksMap[sec.id] = sec.books || [];
      }
      setSectionBooks(booksMap);
    } catch (error) {
      console.error('Error fetching homepage sections:', error);
      // Fallback: try individual requests
      try {
        const res2 = await cachedGet(`${BACKEND_URL}/api/homepage-sections`);
        const sections = res2.data || [];
        setHomepageSections(sections);
        for (const sec of sections) {
          try {
            const bRes = await axios.get(`${BACKEND_URL}/api/homepage-sections/${sec.id}/books`, { timeout: 12000 });
            setSectionBooks(prev => ({ ...prev, [sec.id]: bRes.data || [] }));
          } catch (e) {
            setSectionBooks(prev => ({ ...prev, [sec.id]: [] }));
          }
        }
      } catch (e2) {
        // silent fail
      }
    }
  };

  const fetchBooks = async () => {
    try {
      const booksResponse = await cachedGet(`${BACKEND_URL}/api/books?per_page=100`);
      const allBooks = booksResponse.data.books || booksResponse.data || [];
      
      // Satış dışı kitapları filtrele
      const saleBooks = allBooks.filter(book => {
        const status = book.fields?.['Satış Durumu'];
        return !status || status === 'satista';
      });
      
      const booksWithAuthors = saleBooks.map(book => ({
        ...book,
        authorNames: (Array.isArray(book.fields?.['Yazar İsimleri']) ? book.fields['Yazar İsimleri'] : []).join(', ')
      }));
      
      // Yeni Çıkanlar - Kitap No büyükten küçüğe
      const sortedByBookNo = booksWithAuthors
        .filter(book => book.fields?.['Kitap No'])
        .sort((a, b) => (b.fields['Kitap No'] || 0) - (a.fields['Kitap No'] || 0))
        .slice(0, 12);
      setNewReleases(sortedByBookNo);
      
      // Çok Satanlar - Baskı Sayısı'na göre (çok baskı yapan = popüler)
      const bestSellerBooks = booksWithAuthors
        .filter(book => (book.fields?.['Baskı Sayısı'] || 0) >= 2)
        .sort((a, b) => (b.fields['Baskı Sayısı'] || 0) - (a.fields['Baskı Sayısı'] || 0))
        .slice(0, 12);
      setBestSellers(bestSellerBooks);
      
      // Tekrar Baskılar - Toplam Üretim Adedi > 1
      const reprintBooks = booksWithAuthors
        .filter(book => (book.fields?.['Toplam Üretim Adedi'] || 0) > 1)
        .slice(0, 12);
      setReprints(reprintBooks);
      
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  // Hero slider: use admin-configured sliders; fallback to first 5 new releases
  const hasSliders = sliderItems.length > 0;
  const heroSlides = hasSliders
    ? sliderItems.map(s => ({
        id: s.id,
        book_id: s.book_id,
        title: s.title,
        subtitle: s.subtitle,
        button_text: s.button_text || 'İncele',
        button_link: s.button_link,
        use_custom_banner: s.use_custom_banner,
        banner_image: s.banner_image,
      }))
    : newReleases.slice(0, 5).map(book => ({
        id: book.id,
        book_id: book.airtable_id || book.id,
        title: book.fields?.['Kitap Başlığı'] || '',
        subtitle: book.fields?.['Kitap Alt Başlığı'] || '',
        button_text: 'İncele',
        button_link: bookPath(book),
        use_custom_banner: false,
        banner_image: null,
        authorNames: book.authorNames,
      }));

  // Hero slider auto-rotate
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ backgroundColor: 'var(--theme-bg)' }}>
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto" style={{ borderColor: 'var(--theme-primary)' }}></div>
            <p className="mt-4" style={{ color: 'var(--theme-text-secondary)' }}>{t('home.loading')}</p>
          </div>
        </div>
        {brands.length > 0 && <BrandMarquee brands={brands} />}
        <Footer siteSettings={siteSettings} />
      </div>
    );
  }

  const BookSection = ({ title, books, viewAllLink }) => (
    <section className="py-12" style={{ backgroundColor: 'var(--theme-bg)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold" style={{ color: 'var(--theme-text)', fontFamily: 'var(--theme-font-heading)' }}>{title}</h2>
          <Link
            to={viewAllLink}
            className={`flex items-center font-semibold group hover:opacity-70 transition-opacity ${isRTL ? 'flex-row-reverse' : ''}`}
            style={{ color: 'var(--theme-primary)' }}
          >
            {t('home.viewAll')}
            <ChevronRight size={20} className={`${isRTL ? 'mr-1 group-hover:-translate-x-1 rotate-180' : 'ml-1 group-hover:translate-x-1'} transition-transform`} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
          {books.map((book) => {
            const airtableId = book.airtable_id || book.id;
            const coverImage = optimizedCover(`/covers/${airtableId}.jpg`, 250);
            const coverSet = coverSrcSet(`/covers/${airtableId}.jpg`, [150, 250, 350]);
            const backCoverImage = optimizedCover(`/covers/${airtableId}_back.jpg`, 250);
            const backCoverSet = coverSrcSet(`/covers/${airtableId}_back.jpg`, [150, 250, 350]);

            const bookTitle = book.fields['Kitap Başlığı'] || book.fields['Kitabın Orjinal Adı'] || '';
            const isArchived = book.fields['Arşiv'] === true;

            return (
              <Link key={book.id} to={bookPath(book)} className="group">
                <div className="aspect-[2/3] relative overflow-hidden shadow-md hover:shadow-xl transition-all duration-300" style={{ borderRadius: 'var(--theme-radius)' }}>
                  <img src={coverImage} alt={bookTitle}
                    srcSet={coverSet}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 220px"
                    loading="lazy"
                    className={`w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0 ${isArchived ? 'grayscale' : ''}`}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  <img src={backCoverImage} alt={`${bookTitle} - Arka Kapak`}
                    srcSet={backCoverSet}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 220px"
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${isArchived ? 'grayscale' : ''}`}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  {isArchived && (
                    <div className={`absolute top-2 ${isRTL ? 'right-2' : 'left-2'} z-10`}>
                      <span className="text-white text-xs font-semibold px-2 py-1" style={{ backgroundColor: 'var(--theme-text)', borderRadius: 'var(--theme-radius)' }}>Arşiv</span>
                    </div>
                  )}
                </div>
                <h3 className="mt-3 font-semibold text-sm line-clamp-2 group-hover:opacity-80 transition-opacity" style={{ color: 'var(--theme-text)', fontFamily: 'var(--theme-font-body)' }}>
                  {bookTitle}
                </h3>
                {book.fields['Kitap Alt Başlığı'] && (
                  <p className="text-xs mt-1 line-clamp-1" style={{ color: 'var(--theme-text-secondary)' }}>{book.fields['Kitap Alt Başlığı']}</p>
                )}
                {book.authorNames && (
                  <p className="text-xs mt-1 font-medium" style={{ color: 'var(--theme-text-secondary)' }}>{book.authorNames}</p>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );

  return (
    <div className={`min-h-screen ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ backgroundColor: 'var(--theme-bg)' }}>
      {/* Hero Section with Slider — reserve space ALWAYS to prevent CLS */}
      {heroSlides.length === 0 ? (
        /* Placeholder: same min-height as real slider so layout doesn't shift when slides arrive */
        <section
          className="relative overflow-hidden"
          style={{
            backgroundColor: sliderConfig?.bg_color || 'var(--theme-nav-bg, #1A1A1A)',
            minHeight: '520px',
          }}
          aria-hidden="true"
        />
      ) : sliderConfig?.style === 'animated' ? (
        /* ── Animated Auteur-style Slider ── */
        <section 
          className="relative overflow-hidden" 
          style={{ 
            backgroundColor: sliderConfig.bg_color || 'var(--theme-nav-bg, #1A1A1A)',
            minHeight: '520px'
          }}
          data-testid="animated-hero-slider"
        >
          {heroSlides.map((slide, index) => {
            const bookId = slide.book_id;
            const coverImage = bookId ? optimizedCover(`/covers/${bookId}.jpg`, 800) : null;
            const isActive = index === currentSlide;
            const hasCustomBanner = slide.use_custom_banner && slide.banner_image;
            const textColor = sliderConfig.text_color || '#FFFFFF';
            const subtitleColor = sliderConfig.subtitle_color || 'var(--theme-primary, #7A2E24)';

            return (
              <div
                key={`animated-${slide.id}-${index}`}
                className={`absolute inset-0 transition-opacity duration-700 ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              >
                {hasCustomBanner ? (
                  <>
                    <img src={slide.banner_image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                      <div className="max-w-xl" key={isActive ? `text-active-${index}` : undefined}>
                        {isActive && (
                          <>
                            {slide.title && <h1 className="hero-title-enter text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight" style={{ fontFamily: 'var(--theme-font-heading)', color: textColor }}>{slide.title}</h1>}
                            {slide.subtitle && <p className="hero-desc-enter text-lg sm:text-xl mb-6" style={{ fontFamily: 'var(--theme-font-body)', color: `${textColor}cc` }}>{slide.subtitle}</p>}
                            {slide.button_link && (
                              <Link to={slide.button_link} className="hero-btn-enter inline-block px-8 py-3 font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: 'var(--theme-primary)', color: '#FFFFFF', borderRadius: 'var(--theme-radius, 4px)' }}>
                                {slide.button_text}
                              </Link>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-center w-full" key={isActive ? `grid-active-${index}` : undefined}>
                      {/* Left: Text Content with slide-from-left animation */}
                      <div className={`${isRTL ? 'order-2' : 'order-1'}`}>
                        {isActive && (
                          <>
                            {slide.subtitle && (
                              <p className="hero-subtitle-enter text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: subtitleColor, fontFamily: 'var(--theme-font-body)' }}>
                                {slide.subtitle}
                              </p>
                            )}
                            <h1 className="hero-title-enter text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-[1.1]" style={{ fontFamily: 'var(--theme-font-heading)', color: textColor }}>
                              {slide.title}
                            </h1>
                            {slide.authorNames && (
                              <p className="hero-desc-enter text-base mb-4" style={{ color: `${textColor}99`, fontFamily: 'var(--theme-font-body)' }}>
                                {slide.authorNames}
                              </p>
                            )}
                            <p className="hero-desc-enter text-base sm:text-lg max-w-md mb-8 leading-relaxed" style={{ color: `${textColor}88`, fontFamily: 'var(--theme-font-body)' }}>
                              Keşfetmek için tıklayın
                            </p>
                            {slide.button_link && (
                              <Link
                                to={slide.button_link}
                                className="hero-btn-enter inline-block px-8 py-3.5 font-semibold hover:opacity-90 transition-all hover:translate-y-[-2px]"
                                style={{ backgroundColor: 'var(--theme-primary)', color: '#FFFFFF', borderRadius: 'var(--theme-radius, 4px)', fontFamily: 'var(--theme-font-body)' }}
                              >
                                {slide.button_text || 'İncele'}
                              </Link>
                            )}
                          </>
                        )}
                      </div>

                      {/* Right: Book Cover with slide-from-right animation */}
                      <div className={`${isRTL ? 'order-1' : 'order-2'} hidden md:flex justify-center items-center`}>
                        {isActive && coverImage && (
                          <div className="relative">
                            {/* Shadow/glow effect behind book */}
                            <div className="hero-cover-shadow absolute -inset-4 rounded-2xl" style={{ background: `radial-gradient(ellipse at center, var(--theme-primary, #7A2E24)22 0%, transparent 70%)` }} />
                            <Link to={slide.button_link || '#'}>
                              <img
                                src={coverImage}
                                alt={slide.title}
                                fetchpriority={index === 0 ? "high" : "auto"}
                                loading={index === 0 ? "eager" : "lazy"}
                                decoding="async"
                                className="hero-cover-enter relative w-[300px] lg:w-[340px] rounded-lg shadow-2xl hover:scale-[1.03] transition-transform duration-500"
                                style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Slider Controls - Animated Style */}
          {heroSlides.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className={`absolute ${isRTL ? 'right-6' : 'left-6'} top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full border transition-all hover:scale-110`}
                style={{ borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.05)', color: sliderConfig.text_color || '#FFFFFF' }}
                aria-label="Previous slide"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={nextSlide}
                className={`absolute ${isRTL ? 'left-6' : 'right-6'} top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full border transition-all hover:scale-110`}
                style={{ borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.05)', color: sliderConfig.text_color || '#FFFFFF' }}
                aria-label="Next slide"
              >
                <ChevronRight size={22} />
              </button>

              {/* Progress bar style dots */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className="h-1 rounded-full transition-all duration-500"
                    style={{ 
                      width: index === currentSlide ? '32px' : '12px', 
                      backgroundColor: index === currentSlide ? 'var(--theme-primary, #7A2E24)' : 'rgba(255,255,255,0.3)' 
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      ) : heroSlides.length > 0 ? (
        /* ── Default Slider Style ── */
        <section className="relative h-[500px] overflow-hidden" style={{ backgroundColor: sliderConfig?.bg_color || 'var(--theme-nav-bg, #1A1A1A)' }} data-testid="default-hero-slider">
          {heroSlides.map((slide, index) => {
            const bookId = slide.book_id;
            const coverImage = bookId ? optimizedCover(`/covers/${bookId}.jpg`, 800) : null;
            const hasCustomBanner = slide.use_custom_banner && slide.banner_image;
            const textColor = sliderConfig?.text_color || '#FFFFFF';

            return (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {hasCustomBanner ? (
                  /* Custom banner — full-bleed background */
                  <>
                    <img
                      src={slide.banner_image}
                      alt={slide.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30"></div>
                    <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-16">
                      <div style={{ color: textColor }}>
                        {slide.title && <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: 'var(--theme-font-heading)' }}>{slide.title}</h1>}
                        {slide.subtitle && <p className="text-xl sm:text-2xl mb-5" style={{ fontFamily: 'var(--theme-font-body)', opacity: 0.9 }}>{slide.subtitle}</p>}
                        {slide.button_link && (
                          <Link
                            to={slide.button_link}
                            className="inline-block px-8 py-3 font-semibold hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: 'var(--theme-primary)', color: '#FFFFFF', borderRadius: 'var(--theme-radius, 4px)', fontFamily: 'var(--theme-font-body)' }}
                          >
                            {slide.button_text}
                          </Link>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  /* Auto-generated design from book cover */
                  <>
                    <div className="absolute inset-0 bg-black/50"></div>
                    {coverImage && (
                      <img
                        src={coverImage}
                        alt={slide.title}
                        className="absolute inset-0 w-full h-full object-cover blur-sm"
                      />
                    )}
                    <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center w-full">
                        <div style={{ color: textColor }}>
                          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--theme-font-heading)' }}>{slide.title}</h1>
                          {slide.subtitle && (
                            <p className="text-xl sm:text-2xl mb-6" style={{ fontFamily: 'var(--theme-font-body)', opacity: 0.9 }}>{slide.subtitle}</p>
                          )}
                          {slide.authorNames && (
                            <p className="text-lg mb-6" style={{ opacity: 0.8 }}>{t('bookDetail.author')}: {slide.authorNames}</p>
                          )}
                          {slide.button_link && (
                            <Link
                              to={slide.button_link}
                              className="inline-block px-8 py-3 font-semibold hover:opacity-90 transition-opacity"
                              style={{ backgroundColor: 'var(--theme-primary)', color: '#FFFFFF', borderRadius: 'var(--theme-radius, 4px)', fontFamily: 'var(--theme-font-body)' }}
                            >
                              {slide.button_text || t('home.viewAll')}
                            </Link>
                          )}
                        </div>
                        {coverImage && (
                          <div className="hidden md:block">
                            <Link to={slide.button_link || '#'}>
                              <img
                                src={coverImage}
                                alt={slide.title}
                                className="w-full max-w-sm mx-auto rounded-lg shadow-2xl hover:scale-105 transition-transform duration-300"
                              />
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* Slider Controls */}
          {heroSlides.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-colors`}
                aria-label="Previous slide"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextSlide}
                className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-colors`}
                aria-label="Next slide"
              >
                <ChevronRight size={24} />
              </button>

              {/* Slider Dots */}
              <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 flex ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index === currentSlide ? 'bg-white' : 'bg-white/50'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      ) : null}

      {/* Dynamic Homepage Sections from Admin */}
      {homepageSections.length > 0 ? (
        homepageSections.map((section) => {
          const books = sectionBooks[section.id];
          if (!books || books.length === 0) return null;
          const content = (
            <BookSection
              key={section.id}
              title={section.title}
              books={books}
              viewAllLink={section.view_all_link || '/books'}
            />
          );
          return section.bg_style === 'gray' ? (
            <div key={section.id} style={{ backgroundColor: 'var(--theme-surface, #FAFAFA)' }}>{content}</div>
          ) : (
            <React.Fragment key={section.id}>{content}</React.Fragment>
          );
        })
      ) : sectionsLoaded ? (
        /* Fallback: hardcoded sections only after sections API responded with nothing */
        <>
          <BookSection
            title={t('home.newReleases')}
            books={newReleases.slice(0, 6)}
            viewAllLink="/books"
          />
          {reprints.length > 0 && (
            <div style={{ backgroundColor: 'var(--theme-surface, #FAFAFA)' }}>
              <BookSection
                title={t('home.reprints')}
                books={reprints.slice(0, 6)}
                viewAllLink="/books"
              />
            </div>
          )}
          {bestSellers.length > 0 && (
            <BookSection
              title={t('home.bestSellers')}
              books={bestSellers.slice(0, 6)}
              viewAllLink="/books"
            />
          )}
        </>
      ) : (
        /* Reserve space while sections are loading to avoid CLS */
        <div style={{ minHeight: '900px' }} aria-hidden="true" />
      )}

      {/* Brand Marquee Slider */}
      {brands.length > 0 && <BrandMarquee brands={brands} />}

      {/* About Section */}
      <section className="py-16 text-white" style={{ backgroundColor: 'var(--theme-text, #1A1A1A)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--theme-font-heading)' }}>
            Kaliteli Yayıncılık
          </h2>
          <p className="text-lg text-white/90 max-w-3xl mx-auto mb-8" style={{ fontFamily: 'var(--theme-font-body)' }}>
            Her okuyucu için kaliteli ve ilham verici kitaplar sunuyoruz. 
            Geniş koleksiyonumuzu keşfedin ve okuma deneyiminizi zenginleştirin.
          </p>
          <Link
            to="/books"
            className="inline-block px-8 py-3 font-semibold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: 'var(--theme-primary)', color: '#FFFFFF', borderRadius: 'var(--theme-radius, 4px)' }}
          >
            Tüm Kitapları İncele
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer siteSettings={siteSettings} />
    </div>
  );
};

export default HomePage;